import { getOrdinalNum } from "./utils/get-orginal-num.js";
import { formatRRuleHours, formatRRuleWallClockRange } from "./utils/wall-clock-time.js";
import { formatLength } from "./utils/format-length.js";
import { useMemo } from "react";
import { Tag, WeekDays } from "@therms/atalaya";
import { jsx, jsxs } from "react/jsx-runtime";
import classnames from "classnames";
import { RRule } from "rrule";
//#region src/components/RRule/RRuleTag.tsx
var getWeekFromRRule = (byweekday, freq) => {
	if (freq === RRule.DAILY) return {
		friday: true,
		monday: true,
		saturday: true,
		sunday: true,
		thursday: true,
		tuesday: true,
		wednesday: true
	};
	const _byweekday = byweekday || [];
	const RRuleWeekDayMap = {
		[RRule.FR.weekday]: "friday",
		[RRule.MO.weekday]: "monday",
		[RRule.SA.weekday]: "saturday",
		[RRule.SU.weekday]: "sunday",
		[RRule.TH.weekday]: "thursday",
		[RRule.TU.weekday]: "tuesday",
		[RRule.WE.weekday]: "wednesday"
	};
	const week = {
		friday: false,
		monday: false,
		saturday: false,
		sunday: false,
		thursday: false,
		tuesday: false,
		wednesday: false
	};
	_byweekday.forEach((day) => {
		week[RRuleWeekDayMap[day]] = true;
	});
	return week;
};
var RRuleTag = ({ className, durationMin, rruleString, timezoneLabel, wallClockTime }) => {
	const rrule = useMemo(() => RRule.fromString(rruleString).options, [rruleString]);
	const monthDays = useMemo(() => {
		if (rrule.bymonthday?.length > 0) return rrule.bymonthday.sort((a, b) => a - b).map((md) => getOrdinalNum(md));
		return [];
	}, [rrule.bymonthday]);
	const weekDays = useMemo(() => getWeekFromRRule(rrule?.byweekday, rrule?.freq), [rrule?.byweekday]);
	const isMonthly = rrule.freq === RRule.MONTHLY;
	const hour = useMemo(() => {
		if (rrule.byhour?.length > 0) return formatRRuleHours(rrule.byhour, rrule.byminute?.[0], wallClockTime);
		return "";
	}, [
		rrule.byhour,
		rrule.byminute,
		wallClockTime
	]);
	const range = useMemo(() => {
		if (!durationMin || !(rrule.byhour?.length > 0)) return null;
		const parsed = formatRRuleWallClockRange(rrule.byhour, rrule.byminute?.[0], durationMin, wallClockTime);
		if (!parsed) return null;
		const allDay = durationMin >= 1440;
		return {
			...parsed,
			lengthLabel: allDay ? "All day" : formatLength(durationMin),
			lengthTooltip: `This window runs for ${formatLength(durationMin)}`,
			wraps: parsed.wraps && !allDay
		};
	}, [
		durationMin,
		rrule.byhour,
		rrule.byminute,
		wallClockTime
	]);
	return /* @__PURE__ */ jsxs("div", {
		className: classnames(className, "flex flex-wrap items-baseline gap-px"),
		children: [
			isMonthly ? /* @__PURE__ */ jsx("span", {
				className: "contents",
				children: monthDays.map((monthDay) => /* @__PURE__ */ jsx("span", {
					className: "bg-neutral-faded p-xxs w-[2.7rem] text-center font-bold first:rounded-l last:rounded-r",
					children: monthDay
				}, monthDay))
			}) : /* @__PURE__ */ jsx(WeekDays, { value: weekDays }),
			range ? /* @__PURE__ */ jsxs("span", {
				className: "contents",
				children: [
					/* @__PURE__ */ jsx("span", {
						className: "text-color-subtle mx-xxs font-medium",
						children: "from"
					}),
					/* @__PURE__ */ jsx("span", {
						className: "text-color-strong font-medium",
						children: range.start
					}),
					/* @__PURE__ */ jsx("span", {
						className: "text-color-subtle mx-xxs font-medium",
						children: "to"
					}),
					/* @__PURE__ */ jsx("span", {
						className: "text-color-strong font-medium",
						children: range.end
					}),
					/* @__PURE__ */ jsx("span", {
						className: "ml-xs",
						children: /* @__PURE__ */ jsx(Tag, {
							tooltip: range.lengthTooltip,
							value: range.lengthLabel,
							variant: "info"
						})
					}),
					range.wraps && /* @__PURE__ */ jsx("span", {
						className: "ml-xxs",
						children: /* @__PURE__ */ jsx(Tag, {
							tooltip: "This window ends the next day",
							value: "Overnight",
							variant: "caution"
						})
					})
				]
			}) : /* @__PURE__ */ jsxs("span", {
				className: "contents",
				children: [/* @__PURE__ */ jsx("span", {
					className: "text-color-subtle mx-xxs font-medium",
					children: " at "
				}), /* @__PURE__ */ jsx("span", {
					className: "text-color-strong font-medium",
					children: hour
				})]
			}),
			hour && timezoneLabel && /* @__PURE__ */ jsx("span", {
				className: "text-color-subtle ml-xs text-xs",
				children: timezoneLabel
			})
		]
	});
};
//#endregion
export { RRuleTag };

//# sourceMappingURL=RRuleTag.js.map